import { Badge, type BadgeProps } from '@proerp/ui';
import type { ArticleType, ArticleUnit, Sourcing, StockMovementType } from '@proerp/shared';
import { UNIT_LABELS_DE, formatUnit } from '@proerp/shared';

// Einheiten-Labels sind zentral in @proerp/shared (SSOT für Frontend + PDF).
export const ARTICLE_UNIT_OPTIONS = (Object.keys(UNIT_LABELS_DE) as ArticleUnit[]).map((value) => ({
  value,
  label: UNIT_LABELS_DE[value],
}));

export function unitLabel(unit: ArticleUnit): string {
  return UNIT_LABELS_DE[unit];
}

/**
 * Einheit lesbar (tolerant): bekannte Enum-Werte → Label, Freitext unverändert.
 * Für die Anzeige von Snapshot-/Freitext-Einheiten (Belegpositionen, Bewegungen …).
 */
export { formatUnit };

/** Menge de-DE formatiert + Einheit, z. B. „1.250,5 kg". Tolerant für Freitext-Einheiten. */
export function formatQuantity(qty: number, unit: ArticleUnit | string | null | undefined): string {
  const formatted = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(qty);
  const label = formatUnit(unit);
  return label ? `${formatted} ${label}` : formatted;
}

const SOURCING_META: Record<Sourcing, { tone: BadgeProps['tone']; label: string }> = {
  MAKE: { tone: 'accent', label: 'Eigenfertigung' },
  BUY: { tone: 'neutral', label: 'Zukauf' },
};

export const SOURCING_OPTIONS = (Object.keys(SOURCING_META) as Sourcing[]).map((value) => ({
  value,
  label: SOURCING_META[value].label,
}));

export function SourcingBadge({ sourcing }: { sourcing: Sourcing }) {
  const { tone, label } = SOURCING_META[sourcing];
  return <Badge tone={tone}>{label}</Badge>;
}

const ARTICLE_TYPE_META: Record<ArticleType, { tone: BadgeProps['tone']; label: string; hint: string }> = {
  STANDARD: {
    tone: 'info',
    label: 'Standardartikel',
    hint: 'Lagerartikel — immer am Lager, bei Unterschreitung des Mindestbestands nachbestellen (Ampel/Meldebestand relevant).',
  },
  PROJEKTBEZOGEN: {
    tone: 'neutral',
    label: 'Projektbezogen',
    hint: 'Pro Projekt zukaufen — Bedarf führt zur Bestellung, kein Lagerbestand/Meldebestand.',
  },
};

export const ARTICLE_TYPE_OPTIONS = (Object.keys(ARTICLE_TYPE_META) as ArticleType[]).map((value) => ({
  value,
  label: ARTICLE_TYPE_META[value].label,
}));

export function articleTypeLabel(articleType: ArticleType): string {
  return ARTICLE_TYPE_META[articleType].label;
}

export function articleTypeHint(articleType: ArticleType): string {
  return ARTICLE_TYPE_META[articleType].hint;
}

export function ArticleTypeBadge({ articleType }: { articleType: ArticleType }) {
  const { tone, label } = ARTICLE_TYPE_META[articleType];
  return <Badge tone={tone}>{label}</Badge>;
}

const MOVEMENT_META: Record<StockMovementType, { tone: BadgeProps['tone']; label: string }> = {
  IN: { tone: 'success', label: 'Zugang' },
  OUT: { tone: 'warning', label: 'Abgang' },
  ADJUST: { tone: 'info', label: 'Korrektur' },
};

export const MOVEMENT_TYPE_OPTIONS = (Object.keys(MOVEMENT_META) as StockMovementType[]).map(
  (value) => ({ value, label: MOVEMENT_META[value].label }),
);

export function movementTypeLabel(type: StockMovementType): string {
  return MOVEMENT_META[type].label;
}

export function MovementTypeBadge({ type }: { type: StockMovementType }) {
  const { tone, label } = MOVEMENT_META[type];
  return <Badge tone={tone}>{label}</Badge>;
}
